import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { CaseAuditDataService } from './caseaudit-data.service';
import {
  CaseAuditDateGroup,
  CaseAuditRaw,
  CaseAuditViewItem,
} from './caseaudit.models';

@Component({
  selector: 'app-caseaudit',
  templateUrl: './caseaudit.component.html',
  styleUrls: ['./caseaudit.component.scss'],
})
export class CaseauditComponent implements OnInit, AfterViewInit, OnDestroy {
  islabadmin = false;
  auditLoading = false;
  auditLoadFailed = false;

  allItems: CaseAuditViewItem[] = [];
  dateGroups: CaseAuditDateGroup[] = [];
  performedByOptions: string[] = [];
  categoryOptions: string[] = [];
  dateOptions: Array<{ dateKey: string; label: string }> = [];

  typeFilter = 'All';
  performedByFilter = 'All';
  dateFilter = 'All';

  /** Selected event shown in the right detail pane. */
  selectedItem: CaseAuditViewItem | null = null;

  /** Date header height used for pin-stack offsets. */
  readonly stickyHeaderHeightPx = 35;
  /** Column label row height (keep in sync with .audit-event__columns). */
  readonly stickyColumnsHeightPx = 28;

  /** Currently pinned date headers stacked at the top of the timeline. */
  pinnedGroups: CaseAuditDateGroup[] = [];
  /** Pixel height of the pin stack (pinnedGroups.length * headerHeight). */
  pinStackHeight = 0;

  @ViewChild('timelineScroll')
  timelineScroll?: ElementRef<HTMLElement>;

  @ViewChildren('dateSection')
  dateSections!: QueryList<ElementRef<HTMLElement>>;

  private intersectionObserver?: IntersectionObserver;
  private viewReady = false;
  private collapseRebuildTimer: any;
  private pinStackRaf: number | null = null;
  private lastScrollTop = 0;
  private scrollDirection: 'up' | 'down' | 'none' = 'none';
  private timelineScrollEl?: HTMLElement;
  private boundOnTimelineScroll = () => this.onTimelineScroll();
  private readonly scrollDirectionThresholdPx = 2;
  /**
   * Manual expand/collapse locks. Auto scroll must not override while the
   * section is still in view. Cleared when the section fully leaves the viewport.
   */
  private manualLocks = new Map<string, boolean>();
  /** Raw rows kept so timezone changes can rebuild display dates. */
  private rawAuditRows: CaseAuditRaw[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: CommonService,
    private auditDataService: CaseAuditDataService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    @Inject('caseData') public caseData: any
  ) {}

  ngOnInit(): void {
    this.islabadmin = !!this.caseData?.['islabadmin'];
    this.service.timezoneChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.rawAuditRows.length === 0) {
        return;
      }
      void this.applyRawData(this.rawAuditRows).then(() => {
        this.cdr.markForCheck();
      });
    });
    this.loadAuditData();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.setupIntersectionObserver();
    this.dateSections?.changes?.subscribe(() => {
      this.observeDateSections();
      this.schedulePinStackUpdate();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.detachTimelineScrollListener();
    this.intersectionObserver?.disconnect();
    if (this.collapseRebuildTimer) {
      clearTimeout(this.collapseRebuildTimer);
    }
    if (this.pinStackRaf != null) {
      cancelAnimationFrame(this.pinStackRaf);
    }
  }

  private loadAuditData(): void {
    this.auditLoading = true;
    this.auditLoadFailed = false;

    if (
      this.caseData?.CaseGUID === null ||
      this.caseData?.CaseGUID === undefined
    ) {
      this.auditLoading = false;
      this.allItems = [];
      this.dateGroups = [];
      this.rawAuditRows = [];
      return;
    }

    const obj = {
      CaseGUID: this.caseData.CaseGUID,
      DeploymentKey: (
        this.caseData.deploymentKey ||
        this.caseData.DeploymentKey ||
        ''
      )
        .toString()
        .toUpperCase(),
      AccessionNumber: this.caseData.AccessionNumber,
      RequisitionNumber: this.caseData.RequisitionNumber,
    };

    this.service
      .caseAuditDetails(obj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (data) => {
          try {
            const body = data?.responsebody;
            if (body !== '' && body != null) {
              const raw = typeof body === 'string' ? JSON.parse(body) : body;
              await this.applyRawData(Array.isArray(raw) ? raw : []);
            } else {
              await this.applyRawData([]);
            }
            this.auditLoading = false;
            this.cdr.markForCheck();
            setTimeout(() => {
              this.setupIntersectionObserver();
              this.updatePinStack();
            }, 0);
          } catch (error) {
            this.auditLoading = false;
            this.auditLoadFailed = true;
            console.error(error);
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          this.auditLoading = false;
          this.auditLoadFailed = true;
          console.error(error);
          this.cdr.markForCheck();
        },
      });
  }

  private async applyRawData(raw: CaseAuditRaw[]): Promise<void> {
    this.rawAuditRows = raw || [];
    this.allItems = await this.auditDataService.buildViewItems(this.rawAuditRows);
    this.performedByOptions = this.auditDataService.getDistinctPerformedBy(
      this.allItems
    );
    this.categoryOptions = this.auditDataService.getDistinctCategories(
      this.allItems
    );
    this.dateOptions = this.auditDataService.getDistinctDates(this.allItems);
    // Drop date filter if that calendar day no longer exists after TZ shift
    if (
      this.typeFilter !== 'All' &&
      !this.categoryOptions.includes(this.typeFilter)
    ) {
      this.typeFilter = 'All';
    }
    if (
      this.dateFilter !== 'All' &&
      !this.dateOptions.some((d) => d.dateKey === this.dateFilter)
    ) {
      this.dateFilter = 'All';
    }
    this.manualLocks.clear();
    this.pinnedGroups = [];
    this.pinStackHeight = 0;
    this.refreshGroups();
    setTimeout(() => {
      this.observeDateSections();
      this.updatePinStack();
    }, 0);
  }

  onFilterChange(): void {
    this.manualLocks.clear();
    this.pinnedGroups = [];
    this.pinStackHeight = 0;
    this.refreshGroups();
    setTimeout(() => {
      this.observeDateSections();
      this.updatePinStack();
    }, 0);
  }

  private refreshGroups(): void {
    const filtered = this.auditDataService.applyFilters(
      this.allItems,
      this.typeFilter,
      this.performedByFilter,
      this.dateFilter
    );
    const previousExpanded = new Map(
      this.dateGroups.map((g) => [g.dateKey, g.expanded])
    );
    this.dateGroups = this.auditDataService.groupByDate(filtered).map((g, i) => ({
      ...g,
      stickyIndex: i,
      expanded: previousExpanded.has(g.dateKey)
        ? previousExpanded.get(g.dateKey)
        : true,
    }));
    this.selectTopmostVisible();
  }

  isPinned(dateKey: string): boolean {
    return this.pinnedGroups.some((g) => g.dateKey === dateKey);
  }

  /** Column labels stick just under the live pin stack. */
  getColumnsStickyTop(): number {
    return this.pinStackHeight;
  }

  isSelected(item: CaseAuditViewItem): boolean {
    return this.selectedItem?.id === item.id;
  }

  selectItem(item: CaseAuditViewItem, event?: Event): void {
    event?.stopPropagation();
    this.selectedItem = item;
  }

  /** Pick first event in the first expanded date section. */
  private selectTopmostVisible(): void {
    for (const group of this.dateGroups) {
      if (group.expanded && group.items?.length) {
        this.selectedItem = group.items[0];
        return;
      }
    }
    this.selectedItem = null;
  }

  private clearSelectionIfInGroup(group: CaseAuditDateGroup): void {
    if (!this.selectedItem || !group?.items?.length) {
      return;
    }
    if (group.items.some((item) => item.id === this.selectedItem?.id)) {
      this.selectedItem = null;
    }
  }

  toggleDateGroup(group: CaseAuditDateGroup): void {
    group.expanded = !group.expanded;
    if (!group.expanded) {
      this.clearSelectionIfInGroup(group);
      this.collapseGroupRows(group);
    }
    this.manualLocks.set(group.dateKey, group.expanded);
    setTimeout(() => {
      this.observeDateSections();
      this.updatePinStack();
    }, 0);
  }

  /** Clear inline expand flags (legacy) inside a date section. */
  private collapseGroupRows(group: CaseAuditDateGroup): void {
    if (!group?.items?.length) {
      return;
    }
    for (const item of group.items) {
      if (item.expanded) {
        item.expanded = false;
      }
    }
  }

  trackByDateKey(_: number, group: CaseAuditDateGroup): string {
    return group.dateKey;
  }

  trackByItemId(_: number, item: CaseAuditViewItem): number {
    return item.id;
  }

  copyText(value: string, label = 'Value'): void {
    if (value == null || value === '') {
      return;
    }
    const text = String(value);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => {
          this.toastr.success(`${label} copied`, '', {
            timeOut: 2500,
            positionClass: 'toast-bottom-right',
          });
        },
        () => this.fallbackCopy(text, label)
      );
    } else {
      this.fallbackCopy(text, label);
    }
  }

  private fallbackCopy(text: string, label: string): void {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.toastr.success(`${label} copied`, '', {
        timeOut: 2500,
        positionClass: 'toast-bottom-right',
      });
    } catch {
      this.toastr.error('Copy failed', '', {
        timeOut: 2500,
        positionClass: 'toast-bottom-right',
      });
    }
  }

  displayValue(value: any): string {
    if (value == null || value === '') {
      return 'N/A';
    }
    return String(value);
  }

  private setupIntersectionObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    this.attachTimelineScrollListener();
    const root = this.timelineScroll?.nativeElement || null;
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        let changed = false;
        const scrollingDown = this.scrollDirection === 'down';

        for (const entry of entries) {
          const dateKey = (entry.target as HTMLElement).dataset?.['dateKey'];
          if (!dateKey) {
            continue;
          }
          const group = this.dateGroups.find((g) => g.dateKey === dateKey);
          if (!group) {
            continue;
          }

          if (this.manualLocks.has(dateKey)) {
            if (!entry.isIntersecting) {
              this.manualLocks.delete(dateKey);
            }
            continue;
          }

          // Auto-collapse only while scrolling down. Collapsed sections stay
          // collapsed on scroll up — user must expand manually.
          if (scrollingDown && !entry.isIntersecting && group.expanded) {
            group.expanded = false;
            this.clearSelectionIfInGroup(group);
            this.collapseGroupRows(group);
            changed = true;
          }
        }

        if (changed) {
          if (this.collapseRebuildTimer) {
            clearTimeout(this.collapseRebuildTimer);
          }
          this.collapseRebuildTimer = setTimeout(() => {
            this.observeDateSections();
            this.updatePinStack();
            this.cdr.detectChanges();
          }, 180);
        }
      },
      {
        root,
        threshold: [0, 0.08, 0.2],
        rootMargin: '0px',
      }
    );
    this.observeDateSections();
    this.updatePinStack();
  }

  private attachTimelineScrollListener(): void {
    const el = this.timelineScroll?.nativeElement;
    if (!el) {
      return;
    }
    this.detachTimelineScrollListener();
    this.timelineScrollEl = el;
    this.lastScrollTop = el.scrollTop;
    el.addEventListener('scroll', this.boundOnTimelineScroll, {
      passive: true,
    });
  }

  private detachTimelineScrollListener(): void {
    if (!this.timelineScrollEl) {
      return;
    }
    this.timelineScrollEl.removeEventListener(
      'scroll',
      this.boundOnTimelineScroll
    );
    this.timelineScrollEl = undefined;
  }

  private onTimelineScroll(): void {
    const el = this.timelineScrollEl || this.timelineScroll?.nativeElement;
    if (!el) {
      return;
    }
    const top = el.scrollTop;
    const delta = top - this.lastScrollTop;
    if (delta > this.scrollDirectionThresholdPx) {
      this.scrollDirection = 'down';
    } else if (delta < -this.scrollDirectionThresholdPx) {
      this.scrollDirection = 'up';
    }
    this.lastScrollTop = top;
    this.schedulePinStackUpdate();
  }

  private schedulePinStackUpdate(): void {
    if (this.pinStackRaf != null) {
      return;
    }
    this.pinStackRaf = requestAnimationFrame(() => {
      this.pinStackRaf = null;
      this.updatePinStack();
    });
  }

  /**
   * Build a flush pin stack of date headers that have reached the top.
   * Avoids index-based sticky gaps (content peeking above headers).
   */
  private updatePinStack(): void {
    const viewport = this.timelineScrollEl || this.timelineScroll?.nativeElement;
    if (!viewport || !this.dateGroups.length) {
      this.pinnedGroups = [];
      this.pinStackHeight = 0;
      return;
    }

    const vTop = viewport.getBoundingClientRect().top;
    const pinned: CaseAuditDateGroup[] = [];
    let stackH = 0;

    for (const group of this.dateGroups) {
      const section = this.findSectionElement(group.dateKey);
      if (!section) {
        continue;
      }
      const rect = section.getBoundingClientRect();
      const pinLine = vTop + stackH;
      // Pin while section has reached the pin line and still has content below it
      if (rect.top <= pinLine + 0.5 && rect.bottom > pinLine + 1) {
        pinned.push(group);
        stackH += this.stickyHeaderHeightPx;
      }
    }

    const changed =
      pinned.length !== this.pinnedGroups.length ||
      pinned.some((g, i) => g.dateKey !== this.pinnedGroups[i]?.dateKey);

    if (changed || this.pinStackHeight !== stackH) {
      this.pinnedGroups = pinned;
      this.pinStackHeight = stackH;
      this.cdr.detectChanges();
    }
  }

  private findSectionElement(dateKey: string): HTMLElement | null {
    if (!this.dateSections) {
      return null;
    }
    for (const ref of this.dateSections.toArray()) {
      const el = ref?.nativeElement;
      if (el?.dataset?.['dateKey'] === dateKey) {
        return el;
      }
    }
    return null;
  }

  private observeDateSections(): void {
    if (!this.viewReady || !this.intersectionObserver) {
      return;
    }
    this.intersectionObserver.disconnect();
    this.dateSections?.forEach((ref) => {
      if (ref?.nativeElement) {
        this.intersectionObserver.observe(ref.nativeElement);
      }
    });
  }
}
